import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils2";
import { Tractor, Wrench, ArrowRight } from "lucide-react";

/**
 * Create a new scheduled maintenance task from the Tasks page.
 * Prompts the user to pick an equipment, then capture task details.
 * Submits via POST /schedule. If no equipment exists, steers user to Garage.
 */
export default function AddTaskDialog({ open, onClose, onSaved, defaultEquipmentId = "" }) {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    equipment_id: defaultEquipmentId || "",
    task: "",
    project_id: "",
    interval: 30,
    unit: "days",
    recurring: true,
    reminder_on: true,
    notes: "",
    due_date: today,
  });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get("/equipment"),
      api.get("/projects"),
    ])
      .then(([e, p]) => {
        setEquipment(e.data.items || []);
        setProjects(p.data.items || []);
      })
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (defaultEquipmentId) setForm((f) => ({ ...f, equipment_id: defaultEquipmentId }));
  }, [defaultEquipmentId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.equipment_id) { toast.error("Pick equipment first"); return; }
    if (!form.task.trim()) { toast.error("Task name is required"); return; }
    setSaving(true);
    try {
      const dueDate = new Date(form.due_date);
      dueDate.setHours(9, 0, 0, 0);
      await api.post("/schedule", {
        equipment_id: form.equipment_id,
        task: form.task,
        due_date: dueDate.toISOString(),
        interval: parseInt(form.interval) || 30,
        unit: form.unit,
        recurring: form.recurring,
        reminder_on: form.reminder_on,
        notes: form.notes || null,
        project_id: form.project_id || null,
      });
      toast.success("Task added");
      onSaved?.();
      onClose?.();
      setForm({
        equipment_id: "", task: "", project_id: "", interval: 30, unit: "days",
        recurring: true, reminder_on: true, notes: "", due_date: today,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const noEquipment = !loading && equipment.length === 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl rounded-sm">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-1">// New maintenance task</div>
          <DialogTitle className="font-display font-black text-2xl tracking-tight">Schedule a task</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-zinc-500 text-sm">Loading shop…</div>
        ) : noEquipment ? (
          <div className="py-8 text-center">
            <Tractor className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
            <h3 className="font-display font-bold text-lg mb-1">No equipment yet</h3>
            <p className="text-zinc-400 text-sm mb-5">
              Add your first piece of equipment, then we'll let you schedule tasks against it.
            </p>
            <Button
              onClick={() => { onClose(); navigate("/app/equipment/new"); }}
              className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold"
              data-testid="add-task-goto-garage"
            >
              <Wrench className="w-4 h-4 mr-1" /> Add equipment <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Equipment</Label>
              <Select
                value={form.equipment_id}
                onValueChange={(v) => setForm({ ...form, equipment_id: v })}
              >
                <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="new-task-equipment">
                  <SelectValue placeholder="Pick a piece of equipment" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 max-h-72">
                  {equipment.map((e) => (
                    <SelectItem key={e.equipment_id} value={e.equipment_id}>
                      {e.name} {e.equipment_type ? <span className="text-zinc-500">· {e.equipment_type}</span> : null}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Task</Label>
              <Input
                required
                value={form.task}
                onChange={(e) => setForm({ ...form, task: e.target.value })}
                placeholder="Oil change, Grease fittings, Air filter…"
                className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm"
                data-testid="new-task-name"
              />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Project</Label>
              <Select
                value={form.project_id || "none"}
                onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="new-task-project">
                  <SelectValue placeholder="No project" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="none">No project</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Due date</Label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono"
                  data-testid="new-task-date"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Every</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.interval}
                  onChange={(e) => setForm({ ...form, interval: e.target.value })}
                  className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Unit</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="days">Days</SelectItem>
                    <SelectItem value="hours">Hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2">
                <div>
                  <Label className="text-sm">Recurring</Label>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-600">Auto-reschedule</div>
                </div>
                <Switch checked={form.recurring} onCheckedChange={(v) => setForm({ ...form, recurring: v })} />
              </div>
              <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2">
                <div>
                  <Label className="text-sm">Reminders</Label>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-600">Upcoming feed</div>
                </div>
                <Switch checked={form.reminder_on} onCheckedChange={(v) => setForm({ ...form, reminder_on: v })} />
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold"
                data-testid="new-task-save"
              >
                {saving ? "Saving..." : "Add task"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
